/**
 * @voltxampere/web — Live-mode preferences (Sprint 33-4)
 *
 * AYARLAR > Sürekli Akış sub-tab toggles for the Sprint 33-1
 * always-live watcher and the Sprint 33-2 idle detection. Bridges
 * `alwaysLiveEnabledSignal` + `idleDetectionEnabledSignal` to the
 * IDB settings store.
 *
 * Pattern: mirrors `canvas-prefs.ts` (Sprint 23 F1) — single
 * `initLiveModePrefs()` boot path hydrate, fire-and-forget
 * `mergeSettings()` on every signal flip. SettingsRecord additive
 * field (`liveMode`) — no DB version bump per Sprint 30 v2 strategy.
 *
 * Defaults: both signals already initialised to `true` in their
 * defining modules. Hydrate uses `?? true` per-field so v3 settings
 * records (no liveMode field) start with both enabled — matches the
 * in-memory defaults.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { getSettings, mergeSettings } from "@voltxampere/persistence";

import { alwaysLiveEnabledSignal } from "./always-live-state.js";
import { idleDetectionEnabledSignal } from "./idle-state.js";

let initialised = false;

/**
 * Hydrate the always-live + idle-detection signals from the
 * settings store. Called once during boot. Returns the count of
 * fields hydrated for dev-mode logging.
 */
export async function initLiveModePrefs(): Promise<number> {
  if (initialised) return 0;
  initialised = true;
  let hydrated = 0;
  try {
    const settings = await getSettings();
    const liveMode = settings?.liveMode;
    if (liveMode) {
      if (typeof liveMode.alwaysLiveEnabled === "boolean") {
        alwaysLiveEnabledSignal.value = liveMode.alwaysLiveEnabled;
        hydrated++;
      }
      if (typeof liveMode.idleDetectionEnabled === "boolean") {
        idleDetectionEnabledSignal.value = liveMode.idleDetectionEnabled;
        hydrated++;
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA live-mode-prefs] hydrate failed:", err);
    }
  }
  return hydrated;
}

/** Persist the current signal values atomically. */
function persist(): void {
  void mergeSettings({
    liveMode: {
      alwaysLiveEnabled: alwaysLiveEnabledSignal.value,
      idleDetectionEnabled: idleDetectionEnabledSignal.value,
    },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA live-mode-prefs] persist failed:", err);
    }
  });
}

/** Toggle always-live mode (persists immediately). */
export function setAlwaysLiveEnabled(enabled: boolean): void {
  alwaysLiveEnabledSignal.value = enabled;
  persist();
}

/** Toggle idle detection (persists immediately). */
export function setIdleDetectionEnabled(enabled: boolean): void {
  idleDetectionEnabledSignal.value = enabled;
  persist();
}

/** Test-only — reset hydration flag. */
export function __resetLiveModePrefsForTests(): void {
  initialised = false;
}
